import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

interface Component {
  id: string;
  name: string;
  description?: string;
  dimensionId?: string;
  dimensionName?: string;
  displayOrder: number;
  created_by?: string;
  createdBy?: string;
  created_date?: string;
  createdDate?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

const Components: React.FC = () => {
  const { hasPermission, currentUser } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingComponent, setEditingComponent] = useState<Component | null>(null);
  const [componentsData, setComponentsData] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Delete all state
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteStatus, setDeleteStatus] = useState<string | null>(null);

  // File upload state
  const [uploadedComponents, setUploadedComponents] = useState<any[]>([]);
  const [showUploadPreview, setShowUploadPreview] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const canModify = hasPermission('canManageSettings');
  const isAdmin = currentUser?.role === 'Administrator';

  useEffect(() => {
    loadComponentsData();
  }, []);

  const loadComponentsData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Components: Loading from database...');

      const components = await apiService.getComponents();

      console.log('📊 Components: Loaded', components.length, 'components from database');

      setComponentsData(components);
    } catch (error) {
      console.error('Failed to load components data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateComponent = () => {
    console.log('📝 Opening create component form.');
    setEditingComponent(null);
    setFormData({
      name: '',
      description: ''
    });
    setShowCreateForm(true);
  };

  const handleViewComponent = (component: Component) => {
    setEditingComponent(component);
    setFormData({
      name: component.name,
      description: component.description || ''
    });
    setViewMode(true);
    setShowCreateForm(true);
  };

  const handleEditComponent = (component: Component) => {
    setEditingComponent(component);
    setFormData({
      name: component.name,
      description: component.description || ''
    });
    setViewMode(false);
    setShowCreateForm(true);
  };

  const handleSaveComponent = async () => {
    if (!formData.name.trim()) return;

    // Check for duplicate name (case-insensitive)
    const trimmedName = formData.name.trim().toLowerCase();
    const duplicateComponent = componentsData.find(c =>
      c.name.toLowerCase() === trimmedName &&
      (!editingComponent || c.id !== editingComponent.id)
    );

    if (duplicateComponent) {
      alert(`A component with the name "${formData.name.trim()}" already exists. Please use a different name.`);
      return;
    }

    try {
      const componentData = {
        name: formData.name.trim(),
        description: formData.description.trim()
      };

      if (editingComponent) {
        await apiService.updateComponent(editingComponent.id, componentData);
        console.log('✅ Component updated successfully');
      } else {
        await apiService.createComponent(componentData);
        console.log('✅ Component created successfully');
      }

      await loadComponentsData();
      handleCancelForm();
    } catch (error) {
      console.error('Failed to save component:', error);
    }
  };

  const handleDeleteComponent = async (component: Component) => {
    if (!canModify) return;

    const confirmed = window.confirm(`Are you sure you want to delete component "${component.name}"? This will also delete all its attributes.`);
    if (!confirmed) return;

    try {
      await apiService.deleteComponent(component.id);
      console.log('✅ Component deleted successfully');
      await loadComponentsData();
    } catch (error) {
      console.error('Failed to delete component:', error);
    }
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingComponent(null);
    setViewMode(false);
    setFormData({ name: '', description: '' });
  };

  const downloadComponentsTemplate = () => {
    const headers = ['Component Name', 'Description'];
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'components_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadComponentsData = () => {
    if (componentsData.length === 0) {
      alert('No components data to download');
      return;
    }

    const headers = ['Component Name', 'Description'];
    const csvRows = [headers.join(',')];

    componentsData.forEach(comp => {
      const row = [
        `"${(comp.name || '').replace(/"/g, '""')}"`,
        `"${(comp.description || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `components_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseComponentsCSV(text);
    };
    reader.readAsText(file);
  };

  const parseComponentsCSV = (csvText: string) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length <= 1) {
      alert('CSV file is empty or contains only headers');
      return;
    }

    const components: any[] = [];
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length < 2) continue;

      const component = {
        name: values[0],
        description: values[1] || '',
        isDuplicate: componentsData.some(c => c.name.toLowerCase() === values[0].toLowerCase())
      };

      components.push(component);
    }

    if (components.length === 0) {
      alert('No valid components found in CSV file');
      return;
    }

    setUploadedComponents(components);
    setShowUploadPreview(true);
  };

  const processBulkComponentUpload = async () => {
    try {
      const componentsToCreate = uploadedComponents.filter(c => !c.isDuplicate);

      if (componentsToCreate.length === 0) {
        alert('No new components to create. All components already exist.');
        return;
      }

      let createdCount = 0;
      const errors: string[] = [];

      for (const comp of componentsToCreate) {
        try {
          await apiService.createComponent({
            name: comp.name,
            description: comp.description
          });
          createdCount++;
        } catch (err) {
          const errorMsg = `Failed to create component "${comp.name}": ${err instanceof Error ? err.message : 'Unknown error'}`;
          console.error(errorMsg);
          errors.push(errorMsg);
        }
      }

      let message = `Successfully created ${createdCount} out of ${componentsToCreate.length} components`;
      if (errors.length > 0) {
        message += `\n\nErrors encountered:\n${errors.join('\n')}`;
      }

      alert(message);
      await loadComponentsData();
      setShowUploadPreview(false);
      setUploadedComponents([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error creating components:', error);
      alert(`Error creating components: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const cancelBulkUpload = () => {
    setShowUploadPreview(false);
    setUploadedComponents([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteAllData = async () => {
    if (!isAdmin) {
      setDeleteStatus('Only administrators can delete all components');
      return;
    }

    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm deletion');
      return;
    }

    setLoading(true);
    setDeleteStatus('Deleting all components...');

    try {
      let deletedCount = 0;

      for (const component of componentsData) {
        try {
          await apiService.deleteComponent(component.id);
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete component ${component.id}:`, error);
        }
      }

      setDeleteStatus(`Successfully deleted ${deletedCount} components`);
      await loadComponentsData();
      setShowDeleteAllModal(false);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting all components:', error);
      setDeleteStatus('Failed to delete all components. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteAllModal(false);
    setDeleteConfirmText('');
    setDeleteStatus(null);
  };

  // In multi-framework architecture, we don't filter by dimension anymore
  // Components can be used across multiple dimensions in different frameworks
  const filteredComponents = componentsData;

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Components Management</h1>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Components</h3>
            <p>Please wait while we fetch framework components and dimensions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Components Management</h1>
          </div>
          <div className="page-header__actions">
            {canModify && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateComponent}
                >
                  ➕ Add Component
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={downloadComponentsTemplate}
                  title="Download CSV template for bulk component upload"
                >
                  📋 Download Template
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload CSV file with components"
                >
                  📤 Upload Components
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".csv"
                  style={{ display: 'none' }}
                />
              </>
            )}
            {isAdmin && componentsData.length > 0 && (
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteAllModal(true)}
                disabled={loading}
              >
                🗑️ Delete All Data
              </button>
            )}
            {componentsData.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadComponentsData}
                title="Download current components data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Upload Preview Modal */}
      {showUploadPreview && uploadedComponents.length > 0 && (
        <div className="modal-overlay" onClick={cancelBulkUpload}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '600px',
            maxWidth: '800px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>📤 Preview Component Upload</h2>
              <p style={{ marginTop: '8px', marginBottom: '0', color: '#666', fontSize: '14px' }}>
                Review components to be imported. Components marked as duplicates will be skipped.
              </p>
            </div>

            {/* Scrollable table */}
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '24px' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Component Name</th>
                    <th>Description</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedComponents.map((comp, idx) => (
                    <tr key={idx} style={{ backgroundColor: comp.isDuplicate ? '#fff3cd' : 'transparent' }}>
                      <td>{comp.name}</td>
                      <td>{comp.description}</td>
                      <td>
                        {comp.isDuplicate ? (
                          <span style={{ color: '#856404' }}>⚠️ Duplicate</span>
                        ) : (
                          <span style={{ color: '#28a745' }}>✓ New</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Fixed buttons at bottom */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={cancelBulkUpload}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#999';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#ddd';
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: '#333',
                  border: '1px solid #ddd',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={processBulkComponentUpload}
                disabled={uploadedComponents.filter(c => !c.isDuplicate).length === 0}
                onMouseOver={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = '#1565c0';
                  }
                }}
                onMouseOut={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = '#1976d2';
                  }
                }}
                style={{
                  backgroundColor: uploadedComponents.filter(c => !c.isDuplicate).length === 0 ? '#ccc' : '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  cursor: uploadedComponents.filter(c => !c.isDuplicate).length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                Create {uploadedComponents.filter(c => !c.isDuplicate).length} Components
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={handleCancelForm}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '500px',
              maxWidth: '600px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                {viewMode ? 'View Component' : (editingComponent ? 'Edit Component' : 'Create New Component')}
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {viewMode
                  ? 'Component details (read-only)'
                  : (editingComponent
                    ? 'Update component information'
                    : 'Add a new component to the APR framework')
                }
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveComponent(); }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="name" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Component Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={viewMode}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: viewMode ? '#f5f5f5' : 'white',
                    cursor: viewMode ? 'not-allowed' : 'text'
                  }}
                  placeholder="Enter component name"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="description" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Component Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  disabled={viewMode}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    backgroundColor: viewMode ? '#f5f5f5' : 'white',
                    cursor: viewMode ? 'not-allowed' : 'text'
                  }}
                  placeholder="Describe what this component evaluates and its purpose in the assessment framework"
                />
                <small style={{
                  display: 'block',
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  This description helps users understand what aspects this component covers during application assessment.
                </small>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={handleCancelForm}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    backgroundColor: 'transparent',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {viewMode ? 'Close' : 'Cancel'}
                </button>
                {!viewMode && (
                  <button
                    type="submit"
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'white',
                      backgroundColor: '#1976d2',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                  >
                    {editingComponent ? 'Save Component' : 'Create Component'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Flat Components Table */}
      <div className="md-table-container">
        <table className="md-table" style={{ fontSize: '14px', tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '35%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            {canModify && <col style={{ width: '10%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Component</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Description</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Created By</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Created On</th>
              {canModify && <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredComponents.map((component) => (
              <tr key={component.id}>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', wordWrap: 'break-word' }}>
                  {component.name}
                </td>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', wordWrap: 'break-word' }}>
                  {component.description || 'No description available'}
                </td>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                  {component.created_by || component.createdBy || 'System'}
                </td>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                  {(() => {
                    const dateStr = component.created_date || component.createdDate || component.created_at;
                    if (!dateStr) return 'N/A';
                    const date = new Date(dateStr);
                    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                  })()}
                </td>
                {canModify && (
                  <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleViewComponent(component)}
                        title="View component details"
                        style={{
                          padding: '4px 6px',
                          backgroundColor: 'transparent',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: 'auto'
                        }}
                      >
                        👁️
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditComponent(component)}
                        title="Edit component"
                        style={{
                          padding: '4px 6px',
                          backgroundColor: 'transparent',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: 'auto'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteComponent(component)}
                        title="Delete component"
                        style={{
                          padding: '4px 6px',
                          backgroundColor: 'transparent',
                          color: '#d32f2f',
                          border: '1px solid #d32f2f',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: 'auto'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {componentsData.length === 0 && !loading && (
        <div className="empty-state">
          <div className="empty-state-icon">🔧</div>
          <h3>No Components Available</h3>
          <p>No components have been configured yet. Create your first component to start building the assessment framework.</p>
          {canModify && (
            <div className="empty-state-actions">
              <button
                className="btn btn-primary"
                onClick={handleCreateComponent}
              >
                Create Component
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="form-modal">
          <div className="form-modal-content" style={{ maxWidth: '500px' }}>
            <div className="form-modal-header" style={{ backgroundColor: '#dc3545' }}>
              <h2>⚠️ Confirm Delete All Components</h2>
              <button
                className="btn-close"
                onClick={handleCancelDeleteAll}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, color: '#856404', fontWeight: '600', fontSize: '14px' }}>
                  ⚠️ WARNING: This action cannot be undone!
                </p>
                <p style={{ margin: '8px 0 0 0', color: '#856404', fontSize: '13px' }}>
                  This will permanently delete all {componentsData.length} components and their associated attributes from the system.
                </p>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  Type <strong style={{ color: '#dc3545' }}>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #dc3545',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                  autoFocus
                />
              </div>

              <div className="form-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelDeleteAll}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteAllData}
                  disabled={loading || deleteConfirmText !== 'DELETE'}
                >
                  {loading ? 'Deleting...' : 'Delete All Components'}
                </button>
              </div>

              {deleteStatus && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  borderRadius: '4px',
                  backgroundColor: deleteStatus.includes('Failed') || deleteStatus.includes('error') ? '#f8d7da' : '#d4edda',
                  color: deleteStatus.includes('Failed') || deleteStatus.includes('error') ? '#721c24' : '#155724',
                  fontSize: '14px'
                }}>
                  {deleteStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Components;
